import React from "react";
import { Phone, Shield, AlertTriangle, Zap, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { COMPANY, HERO_IMAGES } from "../mock/data";

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/60 via-white to-white">
      <div className="blob bg-emerald-300/40 h-72 w-72 -top-16 -left-16" />
      <div className="blob bg-teal-200/50 h-96 w-96 -bottom-32 -right-24" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-medium text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" />
            Trusted by 200,000+ Android users worldwide
          </div>
          <h1 className="section-title mt-5 text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 leading-[1.05]">
            Your Android Phone is <span className="text-rose-600">Under Attack</span> Every Day
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
            Malware, spyware, and viruses are constantly targeting your device, stealing your data, and slowing down your phone. Don&apos;t wait until it&apos;s too late — protect it now with {COMPANY.brand}.
          </p>

          <div className="mt-7 grid sm:grid-cols-3 gap-3 max-w-xl">
            <Stat icon={<AlertTriangle className="h-4 w-4" />} label="2.5M+ daily malware attacks" />
            <Stat icon={<Shield className="h-4 w-4" />} label="85% Android devices vulnerable" />
            <Stat icon={<Zap className="h-4 w-4" />} label="Your data is at risk" />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md h-12 px-6 text-base pulse-ring">
              <a href={`tel:${COMPANY.phoneRaw}`}>
                <Phone className="h-5 w-5 mr-2" /> Call {COMPANY.phone}
              </a>
            </Button>
            <a href="#pricing" className="inline-flex items-center h-12 px-5 rounded-md border border-slate-300 text-slate-800 font-medium hover:bg-slate-50">
              View Plans
            </a>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            No credit card required · 30-day money-back guarantee
          </p>
        </div>

        <div className="lg:col-span-5 relative fade-up">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/60 bg-white">
            <img
              src={HERO_IMAGES.shield}
              alt="Android phone protected by HuntHeal security shield"
              className="w-full h-[420px] object-cover"
              loading="eager"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/70 to-transparent p-5">
              <div className="flex items-center gap-3 text-white">
                <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">Real-time Protection</p>
                  <p className="text-xs text-emerald-200">Active · 0 threats detected</p>
                </div>
              </div>
            </div>
          </div>
          <FloatingBadge className="-top-4 -left-4" title="99.9%" sub="Detection" />
          <FloatingBadge className="-bottom-4 -right-4" title="< 2%" sub="CPU Usage" />
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, label }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 backdrop-blur px-3 py-2 text-sm text-slate-700">
      <span className="text-emerald-600">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function FloatingBadge({ className = "", title, sub }) {
  return (
    <div className={`absolute ${className} floaty rounded-2xl bg-white shadow-xl border border-slate-100 px-4 py-3`}>
      <p className="text-xl font-extrabold text-emerald-600">{title}</p>
      <p className="text-xs text-slate-500">{sub}</p>
    </div>
  );
}
